import { exportPostmortems, exportSessions } from './ai-tools-api'
import { dayOf } from '../../../../shared/date/local-time'
import { exportQuery } from './ai-tools-words'
import { folderEntries } from './ai-tools-folder'

// The staff download (spec: "Download" on Sessions and Postmortems): every item
// matching the tab's filters, paged client-side through the staff export
// endpoints (never a key) and saved as one .zip the admin makes itself -- the
// server never makes files. Unzipped, it is the folder the tools write locally
// (owner 2026-10-23; ai-tools-folder.js): telemetry/sessions/*.jsonl,
// telemetry/postmortems/*.json, and telemetry/export.json, what was exported
// (the filter, up to when, how many: the single JSONL file's old first line).
//
// The Sessions tab's download also holds those sessions' postmortems, as the
// local folder does: read through the postmortem export with the tab's
// organization, source and received-from (a postmortem arrives with or after
// its session; its status filter is its own, so the tab's is not used), and
// kept when its session is one of those downloaded. The Postmortems tab's holds
// its postmortems alone. The old single JSONL file is gone.

export const KINDS = {
  session: { header: 'ai-tools-sessions', file: 'sessions', items: 'sessions', limit: 50, read: params => exportSessions(params), noun: 'session' },
  postmortem: { header: 'ai-tools-postmortems', file: 'postmortems', items: 'postmortems', limit: 100, read: params => exportPostmortems(params), noun: 'postmortem' }
}

// ai-tools-telemetry-sessions-<from>-<to>.zip: the chosen local dates,
// `beginning` with no from-date, the download day with no to-date.
export const fileName = (kind, fromDay, toDay, now = Date.now()) =>
  `ai-tools-telemetry-${KINDS[kind].file}-${fromDay || 'beginning'}-${toDay || dayOf(now)}.zip`

// UTF-8 bytes of a string, for the "MB so far" line.
export const byteLength = text => {
  let n = 0
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i)
    if (c < 0x80) n += 1
    else if (c < 0x800) n += 2
    else if (c >= 0xd800 && c < 0xdc00) { n += 4; i++ } else n += 3
  }
  return n
}

const itemBytes = item => byteLength(typeof item.lines === 'string' ? item.lines : JSON.stringify(item.body === undefined ? null : item.body))

const isUnlinkedItem = (kind, item) => (kind === 'session'
  ? String(item.lines || '').split('\n').some(line => line.startsWith('{"unlinked":'))
  : typeof item.body === 'string')

// Every page of one export (continue while `next` is set: a page may be short):
// { items, asOf } of the first page, or null when isCancelled() turned true
// (checked after each page). A failure throws (the response).
const readAll = async ({ spec, query, onPage, isCancelled }) => {
  const items = []
  let cursor = ''
  let asOf = ''
  do {
    const page = await spec.read(cursor ? { ...query, cursor } : query)
    if (!asOf) asOf = (page && page.asOf) || ''
    const got = (page && page[spec.items]) || []
    items.push(...got)
    if (isCancelled()) return null
    onPage(got)
    const next = (page && page.next) || ''
    if (next === cursor) break
    cursor = next
  } while (cursor)
  return { items, asOf }
}

// The postmortem export's query for the Sessions tab's sessions.
const postmortemsOfSessionsQuery = filter => {
  const query = { limit: KINDS.postmortem.limit }
  if (filter.organizationId !== undefined) query.organization_id = filter.organizationId
  if (filter.source) query.source = filter.source
  if (filter.from) query.from = filter.from
  return query
}

// Reads what the tab's filter matches and returns { entries (the zip's),
// count (the tab's items), sessions, postmortems, truncated, unlinked, upTo },
// or { cancelled: true }. onProgress({ items, postmortems, bytes }) after each
// page; items counts the tab's own kind.
export const downloadFolder = async ({ kind, filter, onProgress = () => {}, isCancelled = () => false, now = () => new Date() }) => {
  const spec = KINDS[kind]
  let bytes = 0
  let items = 0
  let extra = 0
  const progress = () => onProgress({ items, postmortems: kind === 'session' ? extra : items, bytes })

  const primary = await readAll({
    spec,
    query: { ...exportQuery(filter), limit: spec.limit },
    onPage: got => { items += got.length; got.forEach(item => { bytes += itemBytes(item) }); progress() },
    isCancelled
  })
  if (!primary) return { cancelled: true }

  let sessions = []
  let postmortems = []
  if (kind === 'session') {
    sessions = primary.items
    if (sessions.length) {
      const ids = new Set(sessions.map(item => item.id))
      const theirs = await readAll({
        spec: KINDS.postmortem,
        query: postmortemsOfSessionsQuery(filter),
        onPage: got => { got.filter(item => ids.has(item.sessionId)).forEach(item => { extra++; bytes += itemBytes(item) }); progress() },
        isCancelled
      })
      if (!theirs) return { cancelled: true }
      postmortems = theirs.items.filter(item => ids.has(item.sessionId))
    }
  } else {
    postmortems = primary.items
  }

  const all = [...sessions.map(item => ['session', item]), ...postmortems.map(item => ['postmortem', item])]
  const truncated = sessions.filter(item => item.truncated === true).length
  const unlinked = all.filter(([k, item]) => isUnlinkedItem(k, item)).length
  const info = {
    export: spec.header,
    version: 1,
    exportedAt: now().toISOString(),
    upTo: primary.asOf,
    filter,
    count: primary.items.length,
    truncated,
    unlinked,
    sessions: sessions.length,
    postmortems: postmortems.length,
    layout: 'telemetry/sessions/<yyyy-MM-dd>_<session>.jsonl, telemetry/postmortems/<yyyy-MM-dd>_<HHmmss>_<session>.json'
  }
  return { entries: folderEntries({ sessions, postmortems, info }), count: primary.items.length, sessions: sessions.length, postmortems: postmortems.length, truncated, unlinked, upTo: primary.asOf }
}

// Saves a Blob as a file: an object URL and a temporary link, revoked after.
export const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 100)
}
